import { CaretRight, Check, CheckCircle, Megaphone, Minus, SmileySad } from '@phosphor-icons/react'
import { Button, Icon } from '@shopflo/ui'
import { AnimatePresence, motion } from 'motion/react'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useUser } from 'src/lib/contexts/UserContext'
import { permissionType, ADS_IQ_IMAGE, ADS_IQ_FEATURES_ON, ADS_IQ_FEATURES_OFF } from 'src/lib/utils/constants'
import { classNames } from 'src/lib/utils/helpers'

const contentTransition = { duration: 0.3, ease: 'easeOut' as const }

const StaggeredWords = ({
  text,
  baseDelay = 0,
  wordDelay = 0.035
}: {
  text: string
  baseDelay?: number
  wordDelay?: number
}) => {
  const words = text.split(' ')

  return (
    <>
      {words.map((word, index) => (
        <motion.span
          key={`${word}-${index}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: baseDelay + index * wordDelay,
            duration: 0.2
          }}
          className='inline-block'
        >
          {word}
          {index < words.length - 1 ? '\u00A0' : ''}
        </motion.span>
      ))}
    </>
  )
}

const AdsIqToggle = ({
  checked,
  onChange
}: {
  checked: boolean
  onChange: (value: boolean) => void
}) => (
  <button
    type='button'
    role='switch'
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className='relative flex h-9 w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-full p-1 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]'
  >
    <div
      aria-hidden
      className='absolute inset-0 rounded-full'
      style={{
        backgroundImage: checked
          ? 'linear-gradient(268.66deg, rgb(194, 214, 255) 71.052%, rgba(221, 233, 255, 0) 108.75%)'
          : 'linear-gradient(268.66deg, rgb(229, 231, 235) 18.726%, rgba(221, 233, 255, 0) 108.75%)'
      }}
    />
    <div
      aria-hidden
      className='pointer-events-none absolute inset-0 rounded-full shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.1),inset_0px_0px_0px_2px_rgba(255,255,255,0.4)]'
    />
    <div className='relative h-7 w-11'>
      <div
        className={classNames(
          'absolute top-0 flex size-7 items-center justify-center overflow-hidden rounded-full bg-white transition-transform duration-200 ease-in-out',
          checked
            ? 'translate-x-4 shadow-[-2px_-4px_4px_0px_rgba(1,204,167,0.15),-1px_0px_2px_0px_rgba(71,96,255,0.3),-2px_0px_8px_0px_rgba(71,96,255,0.3),0px_0px_0px_1px_rgba(10,13,18,0.05),0px_1px_2px_0px_rgba(10,13,18,0.05)]'
            : 'translate-x-0 shadow-[0px_0px_0px_1px_rgba(10,13,18,0.05),0px_1px_2px_0px_rgba(10,13,18,0.05)]'
        )}
      >
        <div className='pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_0px_0px_1px_white]' />
        {checked ? (
          <Icon size='sm' IconComponent={Check} iconProps={{ weight: 'bold' }} className='relative text-indigo-400' />
        ) : (
          <Icon size='sm' IconComponent={Minus} iconProps={{ weight: 'bold' }} className='relative text-zinc-400' />
        )}
      </div>
    </div>
  </button>
)

const AdsIqRealEstate: React.FC = () => {
  const { t } = useLocale()
  const { canAccess, user } = useUser()
  const router = useRouter()
  const [adsIqEnabled, setAdsIqEnabled] = useState(true)
  const [imageLoaded, setImageLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  const featureKeys = adsIqEnabled ? ADS_IQ_FEATURES_ON : ADS_IQ_FEATURES_OFF
  const toggleTitle = t(adsIqEnabled ? 'ads_iq_landing.toggle_title' : 'ads_iq_landing.toggle_title_off')
  const FeatureIcon = adsIqEnabled ? CheckCircle : SmileySad
  const featureIconClass = adsIqEnabled ? 'text-indigo-400' : 'text-zinc-400'

  useEffect(() => {
    if (!user) return
    if (canAccess(permissionType.TAB_ADS_IQ, 0)) {
      router.replace('/ads-iq')
    }
  }, [user, canAccess, router])

  // Handle already-cached images where onLoad won't fire
  useEffect(() => {
    if (imgRef.current?.complete) {
      setImageLoaded(true)
    }
  }, [])

  if (!user || canAccess(permissionType.TAB_ADS_IQ, 0)) return null

  return (
    <div className='flex w-full justify-center p-8 px-4 pt-24'>
      <div className='z-50 flex gap-8 pl-12'>
        <div className='flex w-[420px] flex-col pt-12'>
          <div className='flex items-center gap-1'>
            <Icon size='md' IconComponent={Megaphone} iconProps={{ weight: 'fill' }} className='text-zinc-500' />
            <p className='text-text-xxs-semibold text-zinc-500'>{t('ads_iq.title').toUpperCase()}</p>
          </div>

          <div className='mt-3 w-full'>
            <h3 className='w-full text-display-md-semibold text-zinc-800'>{t('ads_iq_landing.header')}</h3>
          </div>

          <div className='mt-4 w-full'>
            <p className='w-full text-wrap pr-4 text-text-sm-medium text-zinc-800'>{t('ads_iq_landing.description')}</p>
          </div>

          <div className='mt-6'>
            <Button
              rightIcon={CaretRight}
              variant='primary'
              size='md'
              onClick={() => window.open('https://forms.gle/rqiy86kuS6YG8ZeU7', '_blank')}
            >
              {t('ads_iq_landing.get_early_access')}
            </Button>
          </div>

          <div className='py-12'>
            <hr className='border-zinc-200' />
          </div>

          <div className='flex w-full flex-col gap-4'>
            <div className='flex h-12 items-center gap-2 px-2'>
              <AdsIqToggle checked={adsIqEnabled} onChange={setAdsIqEnabled} />
              <p className='text-text-md-semibold text-zinc-800 mix-blend-multiply'>{toggleTitle}</p>
            </div>

            <AnimatePresence mode='wait'>
              <motion.div
                key={adsIqEnabled ? 'ads-iq-on' : 'ads-iq-off'}
                initial={{ opacity: 0, filter: 'blur(4px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(4px)' }}
                transition={contentTransition}
                className='flex w-full flex-col gap-1.5'
              >
                {featureKeys.map((featureKey, rowIndex) => (
                  <div key={featureKey} className='flex w-full items-center gap-1 overflow-hidden'>
                    <div className='flex w-9 shrink-0 items-center justify-center overflow-hidden rounded-[10px] px-2 py-1.5'>
                      <Icon
                        size='md'
                        IconComponent={FeatureIcon}
                        iconProps={{ weight: 'fill' }}
                        className={featureIconClass}
                      />
                    </div>
                    <div className='flex min-w-0 flex-1 flex-col justify-center py-1.5'>
                      <p className='px-1 text-text-sm-medium text-zinc-700'>
                        <StaggeredWords
                          text={t(`ads_iq_landing.${featureKey}`)}
                          baseDelay={0.08 + rowIndex * 0.06}
                        />
                      </p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className='flex flex-col items-center gap-4 min-w-[640px]'>
          <div className='h-auto w-[500px]'>
            <motion.img
              ref={imgRef}
              src={ADS_IQ_IMAGE}
              alt={t('ads_iq.title')}
              className='h-full w-full object-contain'
              onLoad={() => setImageLoaded(true)}
              animate={{
                opacity: imageLoaded ? 1 : 0,
                filter: imageLoaded ? 'blur(0px)' : 'blur(10px)'
              }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdsIqRealEstate
